import React, { useState, useEffect } from 'react';
import { botAPI } from '../services/api';

const DIFFICULTY_COLORS = { beginner: '#4ade80', advanced: '#fb923c', expert: '#ef4444' };
const RISK_COLORS = { low: '#4ade80', medium: '#f59e0b', high: '#ef4444' };
const CATEGORY_ICONS = { 'all': '📋', 'day-trading': '⚡', 'swing-trading': '📈', 'hybrid': '⚖️', 'conservative': '🛡️', 'aggressive': '🎯' };
const CATEGORY_NAMES = { 'all': 'All', 'day-trading': 'Day Trading', 'swing-trading': 'Swing', 'hybrid': 'Hybrid', 'conservative': 'Conservative', 'aggressive': 'Aggressive' };

export default function StrategyTemplates() {
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [filter, setFilter] = useState('all');
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [applying, setApplying] = useState(false);
    const [applied, setApplied] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadTemplates(); loadActive(); }, []);

    const loadTemplates = async () => {
        try {
            const res = await botAPI.getTemplates();
            setTemplates(res.data);
        } catch (err) { console.error('Failed to load templates'); }
        setLoading(false);
    };

    const loadActive = async () => {
        try {
            const res = await botAPI.getActiveTemplate();
            setActiveTemplate(res.data.activeTemplate);
        } catch (err) {}
    };

    const loadDetail = async (id) => {
        try {
            const res = await botAPI.getTemplate(id);
            setDetail(res.data);
            setSelected(id);
        } catch (err) { console.error('Failed to load template detail'); }
    };

    const applyTemplate = async (id) => {
        setApplying(true); setApplied(false);
        try {
            const res = await botAPI.applyTemplate(id);
            if (res.data.success) {
                setActiveTemplate(id);
                setApplied(true);
                setTimeout(() => setApplied(false), 4000);
            }
        } catch (err) { console.error('Failed to apply template'); }
        setApplying(false);
    };

    const filtered = filter === 'all' ? templates : templates.filter(t => t.category === filter);
    const categories = ['all', 'day-trading', 'swing-trading', 'hybrid', 'conservative', 'aggressive'];

    return (
        <div className="min-h-screen bg-bg-primary text-text-primary p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold mb-1">📦 Strategy Templates</h1>
                    <p className="text-text-muted text-sm">Pre-built bot configurations ready to deploy. Pick a strategy, preview settings, and apply with one click.</p>
                </div>

                {/* Applied Banner */}
                {applied && (
                    <div className="mb-4 p-3 bg-accent-green/10 border border-accent-green/30 rounded-lg text-accent-green text-sm font-medium flex items-center gap-2">
                        ✅ Template applied! Restart the bot for changes to take effect.
                    </div>
                )}

                {/* Category Filters */}
                <div className="flex gap-2 mb-6 flex-wrap">
                    {categories.map(cat => (
                        <button key={cat} onClick={() => setFilter(cat)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                filter === cat 
                                    ? 'bg-accent-purple text-white' 
                                    : 'bg-bg-card border border-border-color text-text-secondary hover:bg-bg-tertiary'
                            }`}>
                            {CATEGORY_ICONS[cat]} {CATEGORY_NAMES[cat]}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Templates List */}
                    <div className="lg:col-span-2 space-y-3">
                        {loading ? (
                            <div className="text-center text-text-muted py-8">Loading templates...</div>
                        ) : filtered.length === 0 ? (
                            <div className="text-center text-text-muted py-8">No templates in this category.</div>
                        ) : filtered.map(t => (
                            <div key={t.id} onClick={() => loadDetail(t.id)}
                                className={`card p-4 cursor-pointer transition-all hover:border-accent-purple/50 ${
                                    selected === t.id ? 'border-accent-purple ring-1 ring-accent-purple/30' : ''
                                } ${activeTemplate === t.id ? 'ring-2 ring-accent-green/50' : ''}`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">{t.icon}</span>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold">{t.name}</h3>
                                                {activeTemplate === t.id && (
                                                    <span className="text-xs bg-accent-green/20 text-accent-green px-2 py-0.5 rounded-full">Active</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-text-muted mt-0.5">{t.description}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-3">
                                    <span className="text-xs px-2 py-1 rounded-full" style={{ 
                                        backgroundColor: DIFFICULTY_COLORS[t.difficulty] + '20', 
                                        color: DIFFICULTY_COLORS[t.difficulty] 
                                    }}>
                                        {t.difficulty}
                                    </span>
                                    <span className="text-xs px-2 py-1 rounded-full" style={{ 
                                        backgroundColor: RISK_COLORS[t.riskLevel] + '20', 
                                        color: RISK_COLORS[t.riskLevel] 
                                    }}>
                                        Risk: {t.riskLevel}
                                    </span>
                                    <span className="text-xs px-2 py-1 rounded-full bg-bg-tertiary text-text-muted">
                                        🎯 {t.expectedWinRate}
                                    </span>
                                    <span className="text-xs px-2 py-1 rounded-full bg-bg-tertiary text-text-muted">
                                        ⏱️ {t.holdTime}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Detail Panel */}
                    <div className="lg:col-span-1">
                        {detail ? (
                            <div className="card p-5 sticky top-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-3xl">{detail.icon}</span>
                                    <div>
                                        <h2 className="text-lg font-bold">{detail.name}</h2>
                                        <div className="flex gap-2 mt-1">
                                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ 
                                                backgroundColor: DIFFICULTY_COLORS[detail.difficulty] + '20', 
                                                color: DIFFICULTY_COLORS[detail.difficulty] 
                                            }}>{detail.difficulty}</span>
                                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ 
                                                backgroundColor: RISK_COLORS[detail.riskLevel] + '20', 
                                                color: RISK_COLORS[detail.riskLevel] 
                                            }}>Risk: {detail.riskLevel}</span>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-sm text-text-muted mb-4">{detail.description_long}</p>

                                <div className="space-y-2 mb-4">
                                    <h4 className="text-xs font-bold text-text-muted uppercase tracking-wider">Configuration</h4>
                                    <ConfigRow label="Strategies" value={detail.config.strategies.join(', ')} />
                                    <ConfigRow label="Max Positions" value={detail.config.maxPositions} />
                                    <ConfigRow label="Max Daily Trades" value={detail.config.maxDailyTrades} />
                                    <ConfigRow label="Min AI Score" value={detail.config.minAiScore} />
                                    <ConfigRow label="Position Size" value={`${detail.config.positionSizePercent}%`} />
                                    <ConfigRow label="Buying Power Cap" value={`${(detail.config.buyingPowerCap * 100).toFixed(0)}%`} />
                                    <ConfigRow label="ATR Stop" value={`${detail.config.atrStopMultiplier}x`} />
                                    <ConfigRow label="DCA" value={detail.config.dcaConfig?.enabled ? 'Enabled' : 'Disabled'} 
                                        color={detail.config.dcaConfig?.enabled ? '#4ade80' : '#94a3b8'} />
                                    {detail.config.dcaConfig?.enabled && (
                                        <>
                                            <ConfigRow label="  DCA Trigger" value={`${detail.config.dcaConfig.minDropPercent}% drop`} sub />
                                            <ConfigRow label="  DCA Size" value={`${detail.config.dcaConfig.dcaSizePercent}%`} sub />
                                            <ConfigRow label="  DCA Rounds" value={`${detail.config.dcaConfig.maxDcaRounds}x`} sub />
                                        </>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-2 mb-4">
                                    <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                                        <div className="text-xs text-text-muted">Win Rate</div>
                                        <div className="text-sm font-bold">{detail.expectedWinRate}</div>
                                    </div>
                                    <div className="bg-bg-tertiary rounded-lg p-2 text-center">
                                        <div className="text-xs text-text-muted">Hold Time</div>
                                        <div className="text-sm font-bold">{detail.holdTime}</div>
                                    </div>
                                </div>

                                <button onClick={() => applyTemplate(detail.id)} disabled={applying}
                                    className={`w-full py-3 rounded-lg font-bold text-sm transition-colors ${
                                        activeTemplate === detail.id
                                            ? 'bg-accent-green/20 text-accent-green border border-accent-green/30'
                                            : 'bg-accent-purple text-white hover:bg-accent-purple/80'
                                    }`}>
                                    {applying ? 'Applying...' : activeTemplate === detail.id ? '✅ Currently Active' : '🚀 Apply Template'}
                                </button>

                                {activeTemplate === detail.id && (
                                    <p className="text-xs text-text-muted text-center mt-2">
                                        This template is currently driving your bot config.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="card p-8 text-center text-text-muted">
                                <div className="text-4xl mb-3">👈</div>
                                <p className="text-sm">Select a template to see details and configuration.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConfigRow({ label, value, color, sub }) {
    return (
        <div className={`flex justify-between items-center ${sub ? 'pl-2' : ''}`}>
            <span className={`text-xs ${sub ? 'text-text-muted/70' : 'text-text-muted'}`}>{label}</span>
            <span className="text-xs font-mono font-medium" style={color ? { color } : {}}>{value}</span>
        </div>
    );
}
